// Sends the typed question unchanged. The chat API attaches the MCP tools.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import axios from 'axios';
import Cookies from 'js-cookie';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/components/ui/button';
import { BACKEND_API_URL } from '@/utils/api';
import useChatStore from '@/stores/chatStore';

const MODELS = [
    { id: 'gemini-3.1-flash-lite', provider: 'gemini', label: 'Gemini 3.1 Flash-Lite' },
    { id: 'gemini-3.5-flash', provider: 'gemini', label: 'Gemini 3.5 Flash' },
    { id: 'gemini-3-flash-preview', provider: 'gemini', label: 'Gemini 3 Flash' },
    { id: 'gemini-3.1-pro-preview', provider: 'gemini', label: 'Gemini 3.1 Pro' },
    { id: 'grok-4', provider: 'grok', label: 'Grok' },
    { id: 'gpt-4.1-mini', provider: 'openai', label: 'GPT-4.1 mini' },
    { id: 'gpt-4.1', provider: 'openai', label: 'GPT-4.1' },
    { id: 'gpt-5-mini', provider: 'openai', label: 'GPT-5 mini' },
    { id: 'gpt-5', provider: 'openai', label: 'GPT-5' },
];

const MENTIONS = ['@task', '@folder', '@channel', '@drive'];

const chatAuth = () => ({
    headers: { Authorization: `Token ${Cookies.get('access_token') || ''}` },
});

const taskPath = (id) => `/folder/${id}`;

const sourcePath = (source) => {
    if (source?.path) return source.path;
    if (source?.kind === 'task' && source.id) return taskPath(source.id);
    if (source?.kind === 'drive' && source.id) {
        return `https://drive.google.com/file/d/${source.id}/view`;
    }
    return '';
};

const linkLabel = (source) => {
    const title = String(source?.title || '').replace(/[[\]()]/g, '').trim();
    if (title && title !== source?.kind) return title.slice(0, 80);
    if (source?.kind === 'drive') return 'Drive file';
    return `${source?.kind || 'source'} ${source?.id || ''}`.trim();
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const withTaskLinks = (text) => String(text || '')
    .replace(/\[task:(\d+)\]/gi, '[$1](/folder/$1)')
    .replace(/(^|[^\w/])task:(\d+)/gi, '$1[$2](/folder/$2)');

const withSourceLinks = (text, sources) => {
    let next = withTaskLinks(text);
    (sources || []).forEach((source) => {
        const path = sourcePath(source);
        if (!path || source.kind === 'task') return;
        const id = escapeRegExp(source.id);
        const kind = escapeRegExp(source.kind || '');
        const linked = `[${linkLabel(source)}](${path})`;
        next = next.replace(new RegExp(`\\[${kind}:${id}\\]`, 'gi'), linked);
        next = next.replace(new RegExp(`(^|[^\\w/])${kind}:${id}(?![A-Za-z0-9_-])`, 'gi'), `$1${linked}`);
    });
    next = next.replace(
        /\[drive:([A-Za-z0-9_-]+)\]/gi,
        (_, id) => `[Drive file](https://drive.google.com/file/d/${id}/view)`,
    );
    return next;
};

const SourceAnchor = ({ source }) => {
    const path = sourcePath(source);
    const label = linkLabel(source);
    if (!path) {
        return <span className="text-muted-foreground">{label}</span>;
    }
    if (path.startsWith('/')) {
        return <Link to={path} className="text-primary underline">{label}</Link>;
    }
    return <a href={path} className="text-primary underline" target="_blank" rel="noreferrer">{label}</a>;
};

const answerComponents = {
    a: ({ href, children }) => (
        href?.startsWith('/')
            ? <Link to={href} className="text-primary underline">{children}</Link>
            : <a href={href} className="text-primary underline" target="_blank" rel="noreferrer">{children}</a>
    ),
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="mb-2 list-disc pl-4 last:mb-0">{children}</ul>,
    ol: ({ children }) => <ol className="mb-2 list-decimal pl-4 last:mb-0">{children}</ol>,
    li: ({ children }) => <li className="mb-1">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
};

const DEMAND = "This model is currently in high demand please try another model";
const QUICK_MODELS = new Set([
    "gemini-3.1-pro-preview",
    "grok-4",
    "gpt-4.1-mini",
    "gpt-4.1",
    "gpt-5-mini",
    "gpt-5",
]);
const QUICK_WAIT_MS = 3500;

const AgentChatPanel = () => {
    const open = useChatStore((state) => state.open);
    const sessionId = useChatStore((state) => state.sessionId);
    const draft = useChatStore((state) => state.draft);
    const model = useChatStore((state) => state.model);
    const close = useChatStore((state) => state.close);
    const setSessionId = useChatStore((state) => state.setSessionId);
    const setDraft = useChatStore((state) => state.setDraft);
    const setModel = useChatStore((state) => state.setModel);
    const setProvider = useChatStore((state) => state.setProvider);
    const newChat = useChatStore((state) => state.newChat);

    const [sessions, setSessions] = useState([]);
    const [messages, setMessages] = useState([]);
    const [demandNotice, setDemandNotice] = useState(false);
    const waitTimer = useRef(0);
    const sentAt = useRef(0);
    const sendGeneration = useRef(0);
    const unsent = useRef('');
    const [loading, setLoading] = useState(false);
    const [modelOpen, setModelOpen] = useState(false);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [openSource, setOpenSource] = useState(null);
    const [pollKey, setPollKey] = useState(0);
    const transcriptRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        axios.get(`${BACKEND_API_URL}/api/agent/ask/`, chatAuth()).then((response) => {
            if (cancelled || useChatStore.getState().model) return;
            if (response.data.model) setModel(response.data.model);
            if (response.data.provider) setProvider(response.data.provider);
        }).catch(() => {});
        axios.get(`${BACKEND_API_URL}/api/agent/sessions/`, chatAuth()).then((response) => {
            if (!cancelled) setSessions(response.data.sessions || []);
        }).catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [open, setModel, setProvider]);

    useEffect(() => {
        if (!open) return undefined;
        if (!sessionId) {
            setMessages([]);
            if (!waitTimer.current) setLoading(false);
            return undefined;
        }
        let cancelled = false;
        let timer = 0;
        const tick = () => {
            axios.get(`${BACKEND_API_URL}/api/agent/sessions/${sessionId}/`, chatAuth()).then((response) => {
                if (cancelled) return;
                const data = response.data;
                if (data.status === 'pending') {
                    setMessages(data.turns || []);
                    setLoading(true);
                    timer = window.setTimeout(tick, 1000);
                    return;
                }
                const turns = data.turns || [];
                if (data.status === 'error') {
                    const last = turns[turns.length - 1];
                    const restore = data.failed_question || unsent.current || last?.text || '';
                    holdDemand(restore);
                    return;
                }
                sendGeneration.current += 1;
                window.clearTimeout(waitTimer.current);
                waitTimer.current = 0;
                setDemandNotice(false);
                setLoading(false);
                setMessages(turns);
                if (data.removed) {
                    setDemandNotice(true);
                    setSessions((prev) => prev.filter((item) => item.id !== sessionId));
                }
                refreshSessions();
            }).catch((error) => {
                if (cancelled) return;
                if (error.response?.status === 404) {
                    setSessions((prev) => prev.filter((item) => item.id !== sessionId));
                    setSessionId(null);
                    holdDemand(unsent.current);
                    return;
                }
                timer = window.setTimeout(tick, 1500);
            });
        };
        tick();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [open, sessionId, pollKey]);

    const refreshSessions = () => {
        axios.get(`${BACKEND_API_URL}/api/agent/sessions/`, chatAuth()).then((response) => {
            setSessions(response.data.sessions || []);
        }).catch(() => {});
    };

    useEffect(() => {
        const node = transcriptRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [messages, loading]);

    useEffect(() => {
        if (!modelOpen && !mentionOpen) return undefined;
        const closeMenus = (event) => {
            if (event.target.closest('[data-chat-menu]')) return;
            setModelOpen(false);
            setMentionOpen(false);
        };
        document.addEventListener('pointerdown', closeMenus);
        return () => document.removeEventListener('pointerdown', closeMenus);
    }, [modelOpen, mentionOpen]);

    const holdDemand = (question) => {
        const generation = sendGeneration.current;
        const elapsed = sentAt.current ? Date.now() - sentAt.current : 0;
        const delay = Math.max(0, QUICK_WAIT_MS - elapsed);
        window.clearTimeout(waitTimer.current);
        setLoading(true);
        waitTimer.current = window.setTimeout(() => {
            if (generation !== sendGeneration.current) return;
            waitTimer.current = 0;
            setDemandNotice(true);
            setLoading(false);
            if (question) setDraft(question);
            setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === 'user' && last.text === question) return prev.slice(0, -1);
                return prev;
            });
        }, delay);
    };

    const stop = async () => {
        if (!sessionId) return;
        const response = await axios.post(`${BACKEND_API_URL}/api/agent/sessions/${sessionId}/cancel/`, {}, chatAuth());
        setLoading(false);
        if (response.data?.question) setDraft(response.data.question);
        setPollKey((key) => key + 1);
    };

    const send = async () => {
        const question = draft.trim();
        if (!question || loading) return;
        const selected = MODELS.find((item) => item.id === model) || MODELS[0];
        unsent.current = question;
        sentAt.current = Date.now();
        const generation = sendGeneration.current + 1;
        sendGeneration.current = generation;
        window.clearTimeout(waitTimer.current);
        waitTimer.current = 0;
        setDemandNotice(false);
        setDraft('');
        setMessages((prev) => [...prev, { role: 'user', text: question, sources: [] }]);
        setLoading(true);
        try {
            const response = await axios.post(`${BACKEND_API_URL}/api/agent/chat/`, {
                question,
                session_id: sessionId,
                provider: selected.provider,
                model: selected.id,
            }, chatAuth());
            const nextId = response.data.session_id;
            setSessionId(nextId);
            setPollKey((key) => key + 1);
            if (QUICK_MODELS.has(selected.id)) {
                window.clearTimeout(waitTimer.current);
                waitTimer.current = window.setTimeout(() => {
                    if (generation !== sendGeneration.current) return;
                    waitTimer.current = 0;
                    setDemandNotice(true);
                    setLoading(false);
                    setDraft(question);
                    setMessages((prev) => {
                        const last = prev[prev.length - 1];
                        if (last?.role === 'user' && last.text === question) return prev.slice(0, -1);
                        return prev;
                    });
                    axios.post(`${BACKEND_API_URL}/api/agent/sessions/${nextId}/cancel/`, {}, chatAuth()).catch(() => {});
                }, QUICK_WAIT_MS);
            }
        } catch (error) {
            holdDemand(question);
        }
    };

    const insertMention = (token) => {
        const next = draft.trim() ? `${draft.trim()} ${token} ` : `${token} `;
        setDraft(next);
        setMentionOpen(false);
    };

    if (!open) return null;

    const modelLabel = (MODELS.find((item) => item.id === model) || MODELS[0]).label;

    return (
        <div className="flex h-full w-full min-w-0 overflow-hidden bg-card font-sans">
            <aside className="flex h-full w-[132px] min-h-0 shrink-0 flex-col gap-1 border-r border-border bg-muted/40 p-2">
                <p className="px-1 text-xs font-normal text-foreground">Chats</p>
                <button
                    type="button"
                    onClick={() => {
                        if (sessionId && loading) {
                            axios.post(`${BACKEND_API_URL}/api/agent/sessions/${sessionId}/cancel/`, {}, chatAuth());
                        }
                        newChat();
                        setMessages([]);
                        setLoading(false);
                    }}
                    className={`block w-full rounded-md px-2 py-1.5 text-left text-xs font-normal ${
                        sessionId ? 'text-muted-foreground hover:bg-accent' : 'bg-accent text-foreground'
                    }`}
                >
                    New chat
                </button>
                <div className="flex min-h-0 w-full flex-1 flex-col gap-1 overflow-y-auto">
                {sessions.map((session) => (
                    <button
                        key={session.id}
                        type="button"
                        onClick={() => setSessionId(session.id)}
                        className={`block w-full shrink-0 truncate rounded-md px-2 py-1.5 text-left text-xs font-normal ${
                            sessionId === session.id
                                ? 'bg-accent text-foreground'
                                : 'text-muted-foreground hover:bg-accent'
                        }`}
                        title={session.title}
                    >
                        {session.title}
                    </button>
                ))}
                </div>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center justify-between border-b border-border px-3 py-2">
                    <p className="truncate text-xs font-normal text-foreground">
                        {sessions.find((session) => session.id === sessionId)?.title || 'New chat'}
                    </p>
                    <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs font-normal" onClick={close}>
                        Close
                    </Button>
                </div>
                <div ref={transcriptRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-2">
                    {messages.map((turn, index) => (
                        <div key={`${turn.role}-${index}`} className={turn.role === 'user' ? 'flex justify-end' : ''}>
                            <div className={`max-w-[92%] select-text rounded-md px-2 py-1.5 text-[13px] font-normal leading-[1.55] ${
                                turn.role === 'user' ? 'bg-secondary text-foreground' : 'bg-muted text-foreground'
                            }`}>
                                {turn.role === 'assistant' ? (
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={answerComponents}>
                                        {withSourceLinks(turn.text, turn.sources)}
                                    </ReactMarkdown>
                                ) : turn.text}
                            </div>
                            {turn.role === 'assistant' && turn.sources?.length > 0 && (
                                <div className="mt-1">
                                    <button
                                        type="button"
                                        className="text-xs font-normal text-muted-foreground"
                                        onClick={() => setOpenSource(openSource === index ? null : index)}
                                    >
                                        {turn.sources.length} source{turn.sources.length === 1 ? '' : 's'} · Review
                                    </button>
                                    {openSource === index && (
                                        <ul className="mt-1 space-y-1">
                                            {turn.sources.map((source) => (
                                                <li key={`${source.kind}-${source.id}`} className="text-[13px] font-normal leading-[1.55]">
                                                    <SourceAnchor source={source} />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {loading && (
                        <div className="flex justify-start" aria-label="The model is thinking">
                            <div className="flex items-center gap-1 rounded-md bg-muted px-3 py-2">
                                {[0, 150, 300].map((delay) => (
                                    <span
                                        key={delay}
                                        className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground"
                                        style={{ animationDelay: `${delay}ms` }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="relative border-t border-border p-2">
                    {demandNotice && (
                        <div
                            role="status"
                            className="mb-2 flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/15 px-2 py-1.5 text-[13px] font-normal leading-[1.45] text-amber-100"
                        >
                            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
                            <p className="min-w-0 flex-1">{DEMAND}</p>
                            <button
                                type="button"
                                aria-label="Dismiss"
                                className="shrink-0 px-1 text-base leading-none text-amber-100"
                                onClick={() => setDemandNotice(false)}
                            >
                                ×
                            </button>
                        </div>
                    )}
                    {modelOpen && (
                        <div data-chat-menu className="absolute bottom-16 left-2 z-10 w-56 rounded-md border border-border bg-popover p-1">
                            {MODELS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="block w-full rounded px-2 py-1 text-left text-xs font-normal hover:bg-accent"
                                    onClick={() => {
                                        setModel(item.id);
                                        setProvider(item.provider);
                                        setModelOpen(false);
                                    }}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    )}
                    {mentionOpen && (
                        <div data-chat-menu className="absolute bottom-16 right-2 z-10 w-36 rounded-md border border-border bg-popover p-1">
                            {MENTIONS.map((token) => (
                                <button
                                    key={token}
                                    type="button"
                                    className="block w-full rounded px-2 py-1 text-left text-xs font-normal hover:bg-accent"
                                    onClick={() => insertMention(token)}
                                >
                                    {token}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="rounded-lg border border-border bg-background p-2">
                        <textarea
                            value={draft}
                            rows={2}
                            placeholder="Ask about tasks, mentions, or Drive text"
                            onPointerDown={() => {
                                setModelOpen(false);
                                setMentionOpen(false);
                            }}
                            onChange={(event) => {
                                setModelOpen(false);
                                setMentionOpen(false);
                                setDraft(event.target.value);
                            }}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter' || event.shiftKey) return;
                                event.preventDefault();
                                send();
                            }}
                            className="w-full resize-none bg-transparent text-[13px] font-normal leading-[1.55] text-foreground outline-none placeholder:text-muted-foreground"
                        />
                        <div className="mt-1 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-normal text-foreground">Ask</span>
                                <button
                                    type="button"
                                    data-chat-menu
                                    className="rounded-full px-2 py-0.5 text-xs font-normal text-muted-foreground hover:bg-accent"
                                    onClick={() => {
                                        setModelOpen(!modelOpen);
                                        setMentionOpen(false);
                                    }}
                                >
                                    {modelLabel}
                                </button>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    data-chat-menu
                                    className="h-auto px-2 py-0.5 text-xs font-normal"
                                    onClick={() => {
                                        setMentionOpen(!mentionOpen);
                                        setModelOpen(false);
                                    }}
                                >
                                    @
                                </Button>
                                {loading ? (
                                    <Button variant="secondary" size="sm" className="h-auto px-2 py-0.5 text-xs font-normal" onClick={stop}>
                                        Stop
                                    </Button>
                                ) : (
                                    <Button size="sm" className="h-auto px-2 py-0.5 text-xs font-normal" onClick={send}>
                                        Send
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentChatPanel;
